import { StandaloneComponent } from '@/types/component';
import { getSite } from '@/utils/getSite';
import { isDev } from '@/utils/is/isDev';
import { getOcelotConfig } from 'lib/configs/ocelot';
import Head from 'next/head';
import Script from 'next/script';
import { useEffectOnce } from 'react-use';

export const StandaloneConsent: StandaloneComponent = () => {
  const site = getSite();
  const isSvenskDamOrHant = ['svenskdam', 'hant'].includes(site);
  const oneTrustId = getOcelotConfig('cmp.onetrust_id');
  const didomiSeNoticeid = getOcelotConfig('cmp.didomi_notice_id');
  const didomiSeApiKey = getOcelotConfig('cmp.didomi_api_key');
  const useDidomiCmp = didomiSeNoticeid !== '';
  const consentVersion = useDidomiCmp ? '2.4.1' : `2.3.15${isSvenskDamOrHant ? `${site}` : ''}`;
  const consentBasePath = `/common/js`;
  const consentPath = `${consentBasePath}/consent`;
  const componentVersion = '05';
  const cacheBuster = `v=${consentVersion}-${componentVersion}`;
  const didomiCMpPath = `${consentBasePath}/cmp/didomi.js?${cacheBuster}`;

  const consentConfUrl = `${consentPath}/consent-conf.js?${cacheBuster}`;
  const otSdkUrl = `https://cdn.cookielaw.org/scripttemplates/otSDKStub.js`;
  const tcfStubUrl = `https://cdn.cookielaw.org/consent/tcf.stub.js`;
  const allerConsentUrl = `${consentPath}/consent-${consentVersion}.js`;
  const didomiCustomConfPath = `${consentBasePath}/cmp/didomi-custom.js?${cacheBuster}`;

  const preload = getOcelotConfig('ads.preload.enabled');

  const loadStubRenderblocking = site.includes('motherhood');

  useEffectOnce(() => {
    window.onetrustId = oneTrustId;
  });

  if (process.env.CONSENT_DISABLED === 'true') {
    return null;
  }

  return (
    <>
      <Head>
        {/* Always preload tcf stub and otStub */}
        {!useDidomiCmp && <link rel="preload" href={tcfStubUrl} as="script" />}
        {!useDidomiCmp && <link rel="preload" href={otSdkUrl} as="script" />}
        {useDidomiCmp && <link rel="preload" href={didomiCMpPath} as="script" />}
        {useDidomiCmp && <link rel="preload" href={didomiCustomConfPath} as="script" />}

        {preload && (
          <>
            {!useDidomiCmp && !isDev && (
              <>
                <link rel="preload" href={consentConfUrl} as="script" />
              </>
            )}
            <link rel="preload" href={allerConsentUrl} as="script" />
          </>
        )}
      </Head>
      { useDidomiCmp && <Script src={didomiCustomConfPath} data-api-key={didomiSeApiKey} data-notice-id={didomiSeNoticeid} /> }
      { useDidomiCmp && <Script src={didomiCMpPath} data-api-key={didomiSeApiKey} data-notice-id={didomiSeNoticeid} /> }

      {/* Consent Stub containing tcfapi */}
      { !useDidomiCmp && <Script data-cfasync="false" src={tcfStubUrl} strategy={loadStubRenderblocking ? "beforeInteractive" : "afterInteractive"} /> }

      {/* CMP - OneTrust consent */}
      { !useDidomiCmp && <Script data-cfasync="false" src={otSdkUrl} data-document-language="true" data-domain-script={`${oneTrustId}`} /> }

      {/* Lock bg-scroll for CMP banner */}
      {!useDidomiCmp && !isDev && <Script src={consentConfUrl} />}

      {/* Aller consent library */}
      <Script src={allerConsentUrl} />
    </>
  );
};
